import { useCallback, useEffect, useRef, useState } from 'react';
import { EMPTY_MODE_PROJECTION, normalizeModeProjection } from './mode-management-state.js';
import { createSettingsRequestOwner } from './settingsRequestOwner.js';

const emptyView = () => ({
  projection: EMPTY_MODE_PROJECTION, loading: false, error: null, updatingModeId: '',
});

const unavailableModeAPI = () => Promise.reject(new Error('mode_api_unavailable'));

export function createModeRequestOwner({ modeAPI, instanceId, onChange }) {
  const owner = createSettingsRequestOwner({
    instanceId, targetType: 'mode', emptyProjection: EMPTY_MODE_PROJECTION,
    normalizeProjection: normalizeModeProjection,
    read: modeAPI?.getModes?.bind(modeAPI) ?? unavailableModeAPI, write: modeAPI?.updateMode?.bind(modeAPI),
    onChange: ({ updatingId, ...view }) => onChange({ ...view, updatingModeId: updatingId }),
  });
  return { ...owner, modeAPI };
}

export function useModeManagement({ modeAPI, instanceId } = {}) {
  const [accepted, setAccepted] = useState({ instanceId, modeAPI, ...emptyView() });
  const ownerRef = useRef(null);
  useEffect(() => {
    const owner = createModeRequestOwner({ modeAPI, instanceId,
      onChange: (view) => setAccepted({ instanceId, modeAPI, ...view }) });
    ownerRef.current = owner;
    setAccepted({ instanceId, modeAPI, ...emptyView(), loading: Boolean(modeAPI) });
    void owner.refresh().catch(() => {});
    return () => { owner.dispose(); if (ownerRef.current === owner) ownerRef.current = null; };
  }, [instanceId, modeAPI]);
  const refresh = useCallback((force = false) => (
    ownerRef.current?.instanceId === instanceId && ownerRef.current?.modeAPI === modeAPI
      ? ownerRef.current.refresh(force) : Promise.resolve(EMPTY_MODE_PROJECTION)
  ), [instanceId, modeAPI]);
  const toggle = useCallback((item) => (
    ownerRef.current?.instanceId === instanceId && ownerRef.current?.modeAPI === modeAPI ? ownerRef.current.toggle(item) : Promise.resolve(null)
  ), [instanceId, modeAPI]);
  const view = accepted.instanceId === instanceId && accepted.modeAPI === modeAPI ? accepted : emptyView();
  return { ...view, refresh, toggle };
}

export default useModeManagement;
